import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { z } from 'zod';
import { BaseStatFilterSchema } from '../schemas';
import {
	buildStatsSearchParams,
	buildChartSearchParams,
	buildStatsQueryKey,
	buildStatsExportParams,
} from './buildFilterParams';

type BaseStatFilter = z.infer<typeof BaseStatFilterSchema>;

export interface StatsQueryParams {
	filters?: BaseStatFilter;
	page?: number;
	pageSize?: number;
	sortBy?: string;
	sortOrder?: 'asc' | 'desc';
	extraParams?: Record<string, unknown>;
}

export interface ChartQueryParams {
	filters?: BaseStatFilter;
	extraParams?: Record<string, unknown>;
}

const DEFAULT_STALE_TIME = 5 * 60 * 1000;

interface StatsQueryConfig<T> {
	entityKey: string;
	apiFn: (params: URLSearchParams) => Promise<T>;
	filterKeys?: readonly string[];
	staleTime?: number;
}

interface ChartQueryConfig<T> {
	entityKey: string;
	apiFn: (params: URLSearchParams) => Promise<T>;
	filterKeys?: readonly string[];
	staleTime?: number;
}

/**
 * Factory: creates a paginated stats query hook for any entity.
 * Eliminates ~70 lines of duplicated query key + URLSearchParams logic per entity.
 * `buildExportParams` dựng params xuất Excel từ CÙNG input của hook (cùng filterKeys).
 */
export function createStatsQueryHook<T>(config: StatsQueryConfig<T>) {
	const buildExportParams = (params: StatsQueryParams) => buildStatsExportParams({
		...params,
		filterKeys: config.filterKeys,
	});

	return Object.assign(function useStatsQuery(params: StatsQueryParams) {
		const queryKey = buildStatsQueryKey(config.entityKey, {
			...params,
			filterKeys: config.filterKeys,
			extraParams: params.extraParams,
		});

		return useQuery({
			queryKey,
			queryFn: async () => {
				const searchParams = buildStatsSearchParams({
					...params,
					filterKeys: config.filterKeys,
					extraParams: params.extraParams,
				});
				return config.apiFn(searchParams);
			},
			retry: 1,
			refetchOnWindowFocus: false,
			staleTime: config.staleTime ?? DEFAULT_STALE_TIME,
			// keepPreviousData: show stale data while new query loads
			// eslint-disable-next-line @typescript-eslint/no-explicit-any -- NonFunctionGuard<T> unresolvable with generic T
			placeholderData: keepPreviousData as any,
		});
	}, { buildExportParams });
}

/**
 * Factory: creates a chart query hook for any entity.
 * Eliminates ~30 lines of duplicated filter serialization per entity.
 */
export function createChartQueryHook<T>(config: ChartQueryConfig<T>) {
	return function useChartQuery(params?: ChartQueryParams) {
		return useQuery({
			queryKey: [config.entityKey, 'nivo-chart', params],
			queryFn: async () => {
				const searchParams = buildChartSearchParams(
					params?.filters,
					config.filterKeys,
					params?.extraParams
				);
				return config.apiFn(searchParams);
			},
			retry: 1,
			refetchOnWindowFocus: false,
			staleTime: config.staleTime ?? DEFAULT_STALE_TIME,
		});
	};
}
